import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import './HL7Listing.scss';
import { parseHL7 } from '../../utils/hl7Parser';
import Modal from '../BaseComponents/Modal';
import downloadIcon from '../../assets/icons/download.svg';
import viewIcon from '../../assets/icons/eye.svg';
import pdfIcon from '../../assets/icons/format=pdf.svg';
import { axiosInstance } from '../../clients/axiosClient';
import { API_V1_PREFIX, MEMBERS_API_PREFIX } from '../../utils/constants';
import { InuviFileInfo } from '../../types/apiGeneratedTypes';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { generateSingleInuviPdf } from '../../api/members';
import Loader from '../BaseComponents/Loader';


interface HL7File extends InuviFileInfo {
  content?: string;
}

const HL7Listing: React.FC = () => {
  const [files, setFiles] = useState<HL7File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [, setSelectedFile] = useState<HL7File | null>(null);
  const [parsedHL7, setParsedHL7] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(null);
  const location = useLocation();
  const assigned_doctor = JSON.parse(sessionStorage.getItem("assigned_doctor") ?? "false");
  const { admin: { role } } = useSelector((state: RootState) => state.admin);
  const visible = ((role !== "doctor") || (assigned_doctor && role === "doctor"));

  const fetchFiles = useCallback(async () => {
    try {
      setIsLoading(true);
      const memberId = location.pathname.split('/')[2];
      const { data } = await axiosInstance.get(
        `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${memberId}/inuvi-files`
      );
      setFiles(data.files);
    } catch (error) {
      toast.error('Error loading HL7 files');
      console.error('Error fetching files:', error);
    } finally {
      setIsLoading(false);
    }
  }, [location.pathname]);

  const fetchFilesRef = useRef(fetchFiles);
  fetchFilesRef.current = fetchFiles;

  useEffect(() => {
    fetchFilesRef.current();
  }, [location.pathname]);

  const handleViewFile = async (file: HL7File) => {
    try {
      setIsLoadingContent(true);
      setSelectedFile(file);
      setShowViewModal(true);

      // Fetch file content
      const response = await fetch(file.download_url, {
        method: 'GET',
        headers: {
          'Accept': 'text/plain',
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const content = await response.text();
      
      // Parse HL7 content
      const parsed = parseHL7(content);
      setParsedHL7(parsed);
    } catch (error) {
      toast.error('Error loading file content');
      console.error('Error loading file content:', error);
    } finally {
      setIsLoadingContent(false);
    }
  };

  const handleDownloadFile = async (file: HL7File) => {
    try {
      const response = await fetch(file.download_url, {
        method: 'GET',
        headers: {
          'Accept': 'text/plain',
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_key;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error('Error downloading file');
      console.error('Error downloading file:', error);
    }
  };


  const handleDownloadSinglePdf = async (file: HL7File) => {
    try {
      setDownloadingFileId(file.id);
      const memberId = location.pathname.split('/')[2];
      const blob = await generateSingleInuviPdf(memberId, file.id);

      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `report_${file.file_key}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Report downloaded successfully');
      } else {
        toast.error('Failed to download report');
      }
    } catch (error: any) {
      console.error('Error generating report:', error);
      toast.error(error.message || 'Error generating report');
    } finally {
      setDownloadingFileId(null);
    }
  };

  if (isLoading) {
    return <div className="loading">Loading files...</div>;
  }

  return (
    <div className="hl7-listing mt-50">
      <div className="files-table">
        <table>
          <thead>
            <tr>
              <th>File Name</th>
              <th>Uploaded Date</th>
              <th>Assigned Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map((file) => (
              <tr key={file.id}>
                <td>{file.file_key}</td>
                <td>
                  {file.last_modified
                    ? new Date(file.last_modified).toLocaleDateString()
                    : '—'}
                </td>
                <td>
                  {file.assigned_date
                    ? new Date(file.assigned_date).toLocaleDateString()
                    : '—'}
                </td>
                <td className="actions">
                  <button
                    className="action-button view"
                    onClick={() => handleViewFile(file)}
                    title="View File"
                  >
                    <img src={viewIcon} alt="View" />
                  </button>
                  {visible && <button
                    className="action-button download"
                    onClick={() => handleDownloadFile(file)}
                    title="Download File"
                  >
                    <img src={downloadIcon} alt="Download" />
                  </button>}
                  <button
                    className="action-button pdf"
                    onClick={() => handleDownloadSinglePdf(file)}
                    title="Download PDF"
                    disabled={downloadingFileId !== null}
                  >
                    {downloadingFileId === file.id ? (
                      <Loader height="20px" color="black" />
                    ) : (
                      <img src={pdfIcon} alt="PDF" />
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        width="970px"
      >
        <div className="hl7-viewer-modal">
          <h2>HL7 File Content</h2>
          {isLoadingContent ? (
            <div>Loading content...</div>
          ) : parsedHL7 ? (
            <>
              <div className="member-info">
                <h3>Member Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">ID:</span>
                    <span className="value">{parsedHL7.memberInfo.id}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Name:</span>
                    <span className="value">{parsedHL7.memberInfo.name}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Date of Birth:</span>
                    <span className="value">{parsedHL7.memberInfo.dateOfBirth}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Gender:</span>
                    <span className="value">{parsedHL7.memberInfo.gender}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Sample Received:</span>
                    <span className="value">{parsedHL7.memberInfo.dateSampleReceived}</span>
                  </div>
                </div>
              </div>

              <div className="test-results">
                <h3>Test Results</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Test Name</th>
                      <th>Value</th>
                      <th>Unit</th>
                      <th>Reference Range</th>
                      <th>Status</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedHL7.testResults.map((result: any, index: number) => (
                      <tr key={index}>
                        <td>{result.testName}</td>
                        <td>{result.value}</td>
                        <td>{result.unit}</td>
                        <td>{result.referenceRange}</td>
                        <td>{result.status}</td>
                        <td>
                          {result.notes.map((note: string, noteIndex: number) => (
                            <div key={noteIndex} className="note-item">
                              {note}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div>No content available</div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default HL7Listing; 